import { isEqual } from 'lodash-es';
import type { ActorRefFrom } from 'xstate';
import { assertEvent, assign, enqueueActions, fromPromise, log, sendTo, setup } from 'xstate';
import { z } from 'zod';
import type {
    CreateNormalLineItemInput,
    DeleteNormalLineItemInput,
    SalesDraftDetailOutput,
    SalesDraftNormalLineItem,
    SalesDraftOrder,
    SalesDraftPriceInput,
    SalesDraftPriceOutput,
    SalesDraftSalesConfigOutput,
    UpdateNormalLineItemInput,
} from '../../../server/types/drafts-types.ts';
import { salesDiscountSchema } from '../../../server/types/drafts-types.ts';
import { trpcClient } from '../../../utils/trpc-client.ts';
import type { LineItemFormFields } from '../../sales-draft-line-items/hooks/use-line-item-normal-form.ts';
import type { SelectedLineItemProduct } from '../types/normal-line-machine-types.ts';
import type { draftMachine } from './draft-machine.ts';

export const normalLineItemFormSchema = z.object({
    quantity: z.number().min(0.00001, 'Required').max(500000),
    storage_id: z.string().min(1),
    price: z.number(),
    shipping_type_id: z.string(),
    rebate: z.array(salesDiscountSchema).optional(),
    note: z.string(),
    gross_margin: z.number(),
    total: z.number(),
});

export const addNormalItemMachine = setup({
    types: {
        input: {} as {
            selectedProduct: SelectedLineItemProduct;
            draftOrderId: string;
            customerId: string;
            storageId: string;
            shippingTypeId: string;
            quantity?: number;
        },
        context: {} as {
            product: SelectedLineItemProduct;
            draftOrderId: string;
            quantity: number;
            customerId: string;
            storageId: string;
            shippingTypeId: string;
            salesConfig?: SalesDraftSalesConfigOutput;
            newDaftOrder?: SalesDraftOrder;
            error?: any;
        },
        events: {} as
            | { type: 'focus' | 'blur' | 'set.quantity'; payload?: number }
            | {
                  type: 'set.quantity';
                  payload: number;
              },
        output: {} as {
            newDaftOrder: SalesDraftOrder;
        },
    },
    actions: {
        setQuantity: assign(({ event }) => {
            assertEvent(event, 'set.quantity');

            return {
                quantity: event.payload,
            };
        }),
    },
    guards: {
        isValid: ({ context }) => !!context.quantity && context.quantity > 0,
        isQuantityRequired: ({ context }) => !context.quantity,
        isQuantityInvalid: ({ context }) => context.quantity <= 0,
    },
    actors: {
        fetchSalesConfig: fromPromise<SalesDraftSalesConfigOutput, { product_variant_id: string }>(
            async ({ input }) => {
                return trpcClient.platoUser.query.salesDraft.getSalesDraft.getSalesConfig.query({
                    product_variant_id: input.product_variant_id,
                });
            },
        ),
        createNormalItem: fromPromise<SalesDraftDetailOutput, CreateNormalLineItemInput>(
            async ({ input }) => {
                return trpcClient.platoUser.command.salesDraft.lineItems.createNormalLineItem.mutate({
                    product_variant_id: input.product_variant_id,
                    quantity: input.quantity,
                    quantity_unit: input.quantity_unit,
                    draft_order_id: input.draft_order_id,
                    storage_id: input.storage_id,
                    shipping_type_id: input.shipping_type_id,
                    sales_units_per_package_unit: input.sales_units_per_package_unit,
                    partial_packing_type: input.partial_packing_type,
                    price_scale_factor: input.price_scale_factor,
                });
            },
        ),
    },
}).createMachine({
    id: 'addNormalItem',
    context: ({ input }) => ({
        product: input.selectedProduct,
        draftOrderId: input.draftOrderId,
        customerId: input.customerId,
        storageId: input.storageId,
        shippingTypeId: input.shippingTypeId,
        quantity: input.quantity || 0,
    }),
    initial: 'initial',
    states: {
        initial: {
            always: 'fetchingConfig',
        },
        fetchingConfig: {
            invoke: {
                src: 'fetchSalesConfig',
                id: 'fetchSalesConfig',
                input: ({ context }) => ({
                    product_variant_id: context.product.product_variant_id,
                }),
                onDone: {
                    actions: assign({
                        salesConfig: ({ event }) => event.output,
                    }),
                    target: 'configFetched',
                },
            },
        },
        configFetched: {
            always: [
                {
                    // Auto submit if quantity is already set
                    guard: ({ context }) => context.quantity > 0,
                    target: 'submitting',
                },
                {
                    target: 'updating',
                },
            ],
        },
        updating: {
            on: {
                'set.quantity': {
                    actions: 'setQuantity',
                },
                blur: [
                    {
                        guard: 'isValid',
                        target: 'submitting',
                    },
                ],
            },
        },
        submitting: {
            invoke: {
                src: 'createNormalItem',
                id: 'createNormalItem',
                input: ({ context }) => ({
                    customer_id: context.customerId,
                    product_variant_id: context.product.product_variant_id,
                    quantity: context.quantity,
                    quantity_unit:
                        context.salesConfig?.quantity_unit_sales ||
                        context.product.quantity_unit_sales ||
                        context.product.package_unit,
                    draft_order_id: context.draftOrderId,
                    storage_id: context.storageId,
                    shipping_type_id: context.shippingTypeId,
                    sales_units_per_package_unit: context.salesConfig?.sales_units_per_package_unit,
                    partial_packing_type: context.salesConfig?.partial_packing_type,
                    price_scale_factor: context.salesConfig?.price_scale_factor,
                }),
                onDone: {
                    target: 'submitted',
                    actions: assign({
                        newDaftOrder: ({ event }) => event.output.draft_order,
                    }),
                },
                onError: {
                    target: 'error',
                    actions: [
                        log(({ event }) => event.error),
                        assign(({ event }) => {
                            return {
                                error: event.error,
                            };
                        }),
                    ],
                },
            },
        },
        submitted: {
            type: 'final',
        },
        error: {},
    },
    on: {
        focus: {
            target: '.updating',
        },
    },
    output: ({ context }) => ({
        newDaftOrder: context.newDaftOrder!,
    }),
});

export const normalItemMachine = setup({
    types: {
        context: {} as {
            normalLineItem: SalesDraftNormalLineItem;
            draftOrderId: string;
            form: LineItemFormFields;
            parentRef: ActorRefFrom<typeof draftMachine>;
            customerId: string;
        },
        input: {} as {
            normalLineItem: SalesDraftNormalLineItem;
            draftOrderId: string;
            parentRef: ActorRefFrom<typeof draftMachine>;
            customerId: string;
        },
        events: {} as
            | {
                  type: 'set.form.value.input';
                  key: 'price' | 'storage_id' | 'note' | 'shipping_type_id' | 'quantity';
                  value: string | number;
              }
            | {
                  type: 'set.form.value.rebate';
                  code: string;
                  value: number;
              }
            | {
                  type: 'set.form.value.quantity';
                  value: number;
              }
            | {
                  type: 'set.form.value.price';
                  value: number;
              }
            | {
                  type: 'submit.form';
              }
            | {
                  type: 'delete.item';
              }
            | { type: 'reset.price' }
            | { type: 'calculate.total' },
    },
    actors: {
        updateNormalItem: fromPromise<SalesDraftDetailOutput, UpdateNormalLineItemInput>(
            async ({ input }) => {
                return trpcClient.platoUser.command.salesDraft.lineItems.updateNormalLineItem.mutate(input);
            },
        ),
        deleteNormalItem: fromPromise<SalesDraftDetailOutput, DeleteNormalLineItemInput>(
            async ({ input }) => {
                return trpcClient.platoUser.command.salesDraft.lineItems.deleteNormalLineItem.mutate(input);
            },
        ),
        getPrice: fromPromise<SalesDraftPriceOutput, SalesDraftPriceInput>(async ({ input }) => {
            return trpcClient.platoUser.query.salesDraft.getSalesDraft.getPrice.query(input);
        }),
    },
    actions: {
        setFormInput: assign(({ event, context }) => {
            assertEvent(event, 'set.form.value.input');

            return {
                ...context,
                form: {
                    ...context.form,
                    [event.key]: event.value,
                },
            };
        }),
        setFormRebate: assign(({ event, context }) => {
            assertEvent(event, 'set.form.value.rebate');

            const rebate = (context.form.rebate ?? []).map((r) =>
                r.code === event.code ? { ...r, percent: event.value } : r,
            );

            return {
                ...context,
                form: {
                    ...context.form,
                    rebate,
                },
            };
        }),
        itemUpdated: sendTo(
            ({ context }) => context.parentRef,
            ({ context }) => ({
                type: 'lineItems.updated',
                payload: {
                    line_item_id: context.normalLineItem.line_item_id,
                },
            }),
        ),
        assingPriceResponse: assign(({ context }, params: SalesDraftPriceOutput) => {
            const { price } = params;

            return {
                form: {
                    ...context.form,
                    price: price.price,
                    rebate: price.rebate,
                    total: price.total,
                    gross_margin: price.margin,
                },
            };
        }),
        setFormQuantity: enqueueActions(({ enqueue, event }) => {
            assertEvent(event, 'set.form.value.quantity');
            enqueue.raise({
                type: 'set.form.value.input',
                key: 'quantity',
                value: event.value,
            });
        }),
        setFormPrice: enqueueActions(({ enqueue, event }) => {
            assertEvent(event, 'set.form.value.price');

            enqueue.raise({
                type: 'set.form.value.input',
                key: 'price',
                value: event.value,
            });
        }),
    },
    guards: {
        isFormDirty: ({ context }) => {
            const { normalLineItem, form } = context;

            return !isEqual(
                {
                    quantity: normalLineItem.quantity,
                    price: normalLineItem.price,
                    storage_id: normalLineItem.storage_id,
                    note: normalLineItem.note,
                    shipping_type_id: normalLineItem.shipping_type_id,
                    rebate: normalLineItem.rebate,
                    total: normalLineItem.total,
                    gross_margin: normalLineItem.gross_margin,
                },
                {
                    quantity: form.quantity,
                    price: form.price,
                    storage_id: form.storage_id,
                    note: form.note,
                    shipping_type_id: form.shipping_type_id,
                    rebate: form.rebate,
                    total: form.total,
                    gross_margin: form.gross_margin,
                },
            );
        },
    },
}).createMachine({
    id: 'normalLineItem',
    initial: 'editing',
    context: ({ input }) => {
        const { normalLineItem } = input;

        return {
            normalLineItem: input.normalLineItem,
            draftOrderId: input.draftOrderId,
            form: {
                // TODO: validate required values, should be loaded using default headers values
                quantity: normalLineItem.quantity || 0,
                price: normalLineItem.price || 0,
                storage_id: normalLineItem.storage_id || '',
                rebate: normalLineItem.rebate || [],
                note: normalLineItem.note || '',
                shipping_type_id: normalLineItem.shipping_type_id || '',
                gross_margin: normalLineItem.gross_margin || 0,
                total: normalLineItem.total || 0,
            },
            parentRef: input.parentRef,
            customerId: input.customerId,
        };
    },
    states: {
        editing: {
            initial: 'idle',
            states: {
                idle: {},
                debounceCalculateTotal: {
                    after: {
                        500: 'calculatingTotal',
                    },
                },
                calculatingTotal: {
                    invoke: {
                        src: 'getPrice',
                        id: 'getPrice',
                        input: ({ context }) => ({
                            customer_id: context.customerId,
                            product_variant_id: context.normalLineItem.product_variant_id,
                            quantity: context.form.quantity,
                            quantity_unit: context.normalLineItem.quantity_unit,
                            storage_id: context.form.storage_id,
                            rebate: context.form.rebate || [],
                            price: context.form.price,
                        }),
                        onDone: {
                            target: '#normalLineItem.editing',
                            actions: [
                                {
                                    type: 'assingPriceResponse',
                                    params: ({ event }) => event.output,
                                },
                            ],
                        },
                        onError: {
                            target: '#normalLineItem.error',
                        },
                    },
                },
                resettingPrice: {
                    invoke: {
                        id: 'getPrice',
                        src: 'getPrice',
                        input: ({ context }) => ({
                            customer_id: context.customerId,
                            product_variant_id: context.normalLineItem.product_variant_id,
                            quantity: context.form.quantity,
                            quantity_unit: context.normalLineItem.quantity_unit,
                            storage_id: context.form.storage_id,
                            rebate: [],
                        }),
                        onDone: {
                            // price, rebate and quantity should be updated
                            target: '#normalLineItem.editing',
                            actions: [
                                {
                                    type: 'assingPriceResponse',
                                    params: ({ event }) => event.output,
                                },
                            ],
                        },
                        onError: {
                            target: '#normalLineItem.error',
                        },
                    },
                },
            },
            on: {
                'set.form.value.input': {
                    actions: ['setFormInput'],
                },
                'set.form.value.rebate': {
                    target: '.debounceCalculateTotal',
                    reenter: true,
                    actions: ['setFormRebate'],
                },
                'set.form.value.quantity': {
                    target: '.debounceCalculateTotal',
                    reenter: true,
                    actions: [
                        {
                            type: 'setFormQuantity',
                        },
                    ],
                },
                'set.form.value.price': {
                    target: '.debounceCalculateTotal',
                    reenter: true,
                    actions: [
                        {
                            type: 'setFormPrice',
                        },
                    ],
                },
                'submit.form': {
                    guard: 'isFormDirty',
                    target: 'updating',
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ context }) => ({
                                type: 'lineItems.updating',
                                payload: {
                                    line_item_id: context.normalLineItem.line_item_id,
                                },
                            }),
                        ),
                    ],
                },
                'delete.item': {
                    target: 'deleting',
                },
                'reset.price': {
                    target: '.resettingPrice',
                },
                'calculate.total': {
                    target: '.calculatingTotal',
                },
            },
        },
        updating: {
            invoke: {
                id: 'updateNormalItem',
                src: 'updateNormalItem',
                input: ({ context }) => ({
                    // defaults
                    draft_line_item_id: context.normalLineItem.line_item_id,
                    draft_order_id: context.draftOrderId,
                    product_variant_id: context.normalLineItem.product_variant_id,
                    quantity_unit: context.normalLineItem.quantity_unit,
                    external: context.normalLineItem.external,
                    // forms input
                    quantity: context.form.quantity,
                    price: context.form.price,
                    storage_id: context.form.storage_id,
                    note: context.form.note,
                    shipping_type_id: context.form.shipping_type_id,
                    rebate: context.form.rebate ?? [],
                }),
                onDone: {
                    target: 'updated',
                    actions: [
                        {
                            type: 'itemUpdated',
                        },
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => {
                                return {
                                    type: 'lineItems.update.draftOrder',
                                    payload: event.output.draft_order,
                                };
                            },
                        ),
                    ],
                },
                onError: {
                    target: 'error',
                    actions: ['itemUpdated'],
                },
            },
        },
        updated: {
            after: {
                500: 'editing',
            },
        },
        deleting: {
            invoke: {
                id: 'deleteNormalItem',
                src: 'deleteNormalItem',
                input: ({ context }) => ({
                    draft_line_item_id: context.normalLineItem.line_item_id,
                    draft_order_id: context.draftOrderId,
                }),
                onDone: {
                    target: 'deleted',
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => {
                                return {
                                    type: 'lineItems.update.draftOrder',
                                    payload: event.output.draft_order,
                                };
                            },
                        ),
                    ],
                },
                onError: {
                    target: 'error',
                },
            },
        },
        deleted: {
            after: {
                500: 'editing',
            },
        },
        error: {
            after: {
                500: {
                    target: 'editing',
                },
            },
        },
    },
});
